import { installFocusZoomRecovery } from "./lib/focusZoomRecovery";
import { RouteSkeleton } from "./components/Loading";
import { QueryPersistence } from "./lib/query-persistence";
import { photoCache } from "./lib/media-cache";
import { identifyTelemetry, trackScreen, track, reportError } from "./lib/telemetry";
import { entryModule, mayReload, watchHostedUpdates } from "./lib/hosted-updates";
import { watchSystemNavigation } from "./lib/navigation";
import { setCaptureRecoveryScope, startMediaRecovery } from "./lib/native-media-recovery";
import { MascotLoading } from "./MascotLoading";
import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  NavLink,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { supabase, configured, storedSessionUserId } from "./lib/supabase";
import { accountKey, loadAccount } from "./lib/account";
import { isNative, watchNativeEvents } from "./lib/platform";
import {
  notificationPermission,
  notificationRoute,
  savePushToken,
  watchBrowserPush,
} from "./lib/notifications";
import { signOutAccount } from "./features/onboarding/repository";

import { Preferences } from "./features/notifications/Preferences";
import { Icon } from "./components/Icon";
import { PersonAvatar } from "./components/PersonAvatar";
import { links } from "./lib/links";

const Auth = lazy(() =>
  import("./features/auth/Auth").then((m) => ({ default: m.Auth })),
);
const Onboarding = lazy(() =>
  import("./features/onboarding").then((m) => ({ default: m.Onboarding })),
);
const Profile = lazy(() =>
  import("./features/onboarding").then((m) => ({ default: m.Profile })),
);
const ApplicationQueue = lazy(() =>
  import("./features/onboarding").then((m) => ({
    default: m.ApplicationQueue,
  })),
);
const Feed = lazy(() =>
  import("./features/feed").then((m) => ({ default: m.Feed })),
);
// The Post sheet is code-split. Preloading it (on idle, and when a finger lands on Post)
// means the first tap opens the sheet instead of waiting on a download.
const loadComposer = () => import("./features/feed/Feed");
const CreatePost = lazy(() => loadComposer().then(m => ({ default: m.CreatePost })));
/** Shown for the moment the sheet's code resolves: the sheet's own shape, never the splash. */
function ComposerLoading() {
  return <div className="composer-loading" role="status" aria-label="Opening the Post sheet"><div className="composer-loading-sheet"><span /></div></div>;
}
const Discuss = lazy(() =>
  import("./features/community").then((m) => ({ default: m.Discuss })),
);
const Settings = lazy(() =>
  import("./features/settings").then((m) => ({ default: m.Settings })),
);
const Privacy = lazy(() =>
  import("./features/settings").then((m) => ({ default: m.Privacy })),
);
const Inbox = lazy(() =>
  import("./features/notifications/Inbox").then((m) => ({ default: m.Inbox })),
);
const Manage = lazy(() =>
  import("./features/community").then((m) => ({ default: m.Manage })),
);
function HostedUpdates() {
  const location = useLocation();
  const cache = useQueryClient();
  useEffect(() => {
    if (import.meta.env.DEV) return;
    const current = entryModule(document.documentElement.outerHTML);
    if (!current) return;
    const updates = watchHostedUpdates({
      current,
      fetchLatest: async () => {
        const response = await fetch("/", { cache: "no-store", signal: AbortSignal.timeout(8000) });
        return response.ok ? entryModule(await response.text()) : undefined;
      },
      canReload: () => document.visibilityState === "visible" && mayReload(
        window.location.pathname,
        cache.isMutating() > 0,
        !!document.querySelector('input:focus, textarea:focus, [contenteditable="true"]:focus, [role="dialog"]:not([hidden]), dialog[open], .sheet'),
      ),
      alreadyTried: version => sessionStorage.getItem("ll:last-update-attempt") === version,
      reload: version => {
        sessionStorage.setItem("ll:last-update-attempt", version);
        window.location.reload();
      },
    });
    const check = () => { if (document.visibilityState === "visible") void updates.check(); };
    const deferred = window.setInterval(updates.apply, 5000);
    document.addEventListener("visibilitychange", check);
    window.addEventListener("ll:app-resume", check);
    check();
    return () => {
      updates.dispose();
      window.clearInterval(deferred);
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("ll:app-resume", check);
    };
  }, [location.pathname, cache]);
  return null;
}

export function App() {
  useEffect(installFocusZoomRecovery, []);
  useEffect(() => {
    let disposed = false; let cleanup: (() => void) | undefined;
    void watchSystemNavigation().then(stop => { if (disposed) stop(); else cleanup = stop; });
    return () => { disposed = true; cleanup?.(); };
  }, []);
  const location = useLocation();
  const [posting, setPosting] = useState<string | null>(null);
  useEffect(() => {
    const handoff = () => setPosting(null);
    window.addEventListener("ll:feed-composer-open", handoff);
    return () => window.removeEventListener("ll:feed-composer-open", handoff);
  }, []);
  const [pushRefresh, setPushRefresh] = useState(0);
  const [pushError, setPushError] = useState(false);
  const [foreground, setForeground] = useState<{title: string; body: string; data: Record<string, unknown>} | null>(null);
  const [permission, setPermission] = useState("unavailable");
  const [userId, setUserId] = useState<string | null | undefined>(undefined);
  useEffect(() => setPosting(null), [userId, location.key]);
  useEffect(() => {
    if (!userId) return;
    const idle = window.requestIdleCallback ?? ((fn: () => void) => window.setTimeout(fn, 1500));
    idle(() => { void loadComposer().catch(() => {}); });
  }, [userId]);
  const cache = useQueryClient();
  const navigate = useNavigate();
  useEffect(() => {
    let persistence: QueryPersistence | undefined;
    try { persistence = new QueryPersistence(cache, localStorage); } catch { /* Storage disabled. */ }
    let owner: string | null | undefined;
    let authRevision = 0;
    let disposed = false;
    const updateOwner = (next: string | null) => {
      if (disposed) return;
      if (owner !== next) {
        if (owner || next === null) void photoCache.clear();
        if (persistence) persistence.setOwner(next);
        else cache.clear();
        owner = next;
      }
      setUserId(next);
    };
    // A session saved on this device names its account before the token refresh returns, so
    // Home's code and that account's cached data load during the refresh, not after it. If the
    // refresh fails, getSession reports no session and the app moves to sign-in as usual.
    const stored = storedSessionUserId();
    if (stored) {
      void import("./features/feed");
      updateOwner(stored);
    }
    void supabase.auth.getSession().then(({ data }) => {
      if (!authRevision) updateOwner(data.session?.user.id ?? null);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      authRevision++;
      updateOwner(session?.user.id ?? null);
    });
    const flush = () => persistence?.flush();
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", flush);
    return () => {
      disposed = true;
      data.subscription.unsubscribe();
      persistence?.dispose();
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", flush);
    };
  }, [cache]);
  useEffect(() => { if (userId !== undefined) identifyTelemetry(userId); }, [userId]);
  useEffect(() => { if (userId !== undefined) trackScreen(location.pathname); }, [location.pathname, userId]);
  const recoveryOwner = useRef<string | undefined>(undefined);
  const [recoveryError, setRecoveryError] = useState("");
  const account = useQuery({
    queryKey: accountKey,
    queryFn: loadAccount,
    staleTime: 0,
    enabled: !!userId,
  });
  useEffect(() => {
    // The receipt is written before launching the external camera, not inferred
    // from whichever account happens to be active when Android restores us.
    const data = account.data;
    recoveryOwner.current = setCaptureRecoveryScope(
      data?.user.id === userId ? userId : undefined,
      data?.season?.season_id,
    );
    return () => { recoveryOwner.current = undefined; setCaptureRecoveryScope(); };
  }, [userId, account.data?.user.id, account.data?.season?.season_id]);
  useEffect(() => {
    let disposed = false;
    let cleanup: (() => void) | undefined;
    void startMediaRecovery(owner => {
      if (!disposed && owner === recoveryOwner.current) {
        window.dispatchEvent(new Event("ll:incoming-share"));
        navigate("/home?compose=1");
      }
    }, (error, purpose) => { reportError(error, "camera_recovery", {source: purpose}); if (!disposed) setRecoveryError(purpose === "profile"
      ? "Your profile photo capture was interrupted. Please select your photo again to crop and save it."
      : "We couldn't restore your camera capture. Reopen the app to retry. Nothing was posted."); })
      .then(stop => { if (disposed) stop(); else cleanup = stop; })
      .catch((error) => { reportError(error, "camera_recovery_start"); if (!disposed) setRecoveryError("Camera recovery is unavailable. Please reopen the app before taking a photo."); });
    return () => { disposed = true; cleanup?.(); };
  }, [navigate]);
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`entry-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "season_applications",
          filter: `user_id=eq.${userId}`,
        },
        () => void cache.invalidateQueries({ queryKey: accountKey }),
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "memberships",
          filter: `user_id=eq.${userId}`,
        },
        () => void cache.invalidateQueries({ queryKey: accountKey }),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, cache]);
  async function complete() {
    await cache.invalidateQueries({ queryKey: accountKey });
    navigate("/home");
  }
  async function startFirstPost() {
    await cache.invalidateQueries({ queryKey: accountKey });
    navigate("/home?compose=1");
  }
  async function preferencesComplete() {
    setPushError(false);
    setPushRefresh(value => value + 1);
    setPermission(await notificationPermission());
    await cache.invalidateQueries({ queryKey: accountKey });
    navigate(
      location.pathname === "/home" &&
        new URLSearchParams(location.search).get("compose") === "1"
        ? "/home?compose=1"
        : "/home",
    );
  }
  const approved = account.data?.application?.status === "approved";
  const preferencesDone =
    approved &&
    account.data?.profile?.notification_preferences?.reviewed_season_id ===
      account.data?.application?.season_id;
  const canManage =
    account.data?.admin ||
    (approved &&
      account.data?.teams?.some(
        (t: { role: string; status: string }) =>
          t.role === "captain" && t.status === "approved",
      ));
  // Applicants stay on their application or its pending/declined status (and notification
  // preferences) until approved.
  const pendingOnly = !approved && !account.data?.admin;
  // Home's "Set goal" and "Choose your charity" open the profile editor: load its code and data
  // while the app is idle so the tap opens it at once. The data stays in memory only (it holds
  // the shipping address), which is why it is fetched here rather than persisted.
  useEffect(() => {
    if (!userId || !approved) return;
    const idle = window.requestIdleCallback ?? ((fn: () => void) => window.setTimeout(fn, 1500));
    idle(() => {
      void import("./features/onboarding").catch(() => {});
      void import("./features/onboarding/repository")
        .then(m => cache.prefetchQuery({ queryKey: ["season-onboarding"], queryFn: m.loadOnboarding }))
        .catch(() => {});
    });
  }, [userId, approved, cache]);
  useEffect(() => {
    if (!userId) return;
    let disposed = false;
    let cleanup: (() => void) | undefined;
    const refresh = () => {
      void notificationPermission()
        .then(value => { if (!disposed) setPermission(value); })
        .catch(() => {});
      window.dispatchEvent(new Event("ll:app-resume"));
      // Refresh active season/feed/discussion queries on native resume too.
      void cache.refetchQueries({ type: "active", stale: true });
      window.dispatchEvent(new Event("ll:incoming-share"));
    };
    const refreshPermission = () => { void notificationPermission().then(value => { if (!disposed) setPermission(value); }).catch(() => {}); };
    const visible = () => { if (document.visibilityState === "visible") refreshPermission(); };
    window.addEventListener("focus", refreshPermission);
    window.addEventListener("ll:notification-permission-change", refreshPermission);
    document.addEventListener("visibilitychange", visible);
    refresh();
    void watchNativeEvents({
      onForegroundNotification: (notice) => { if (!disposed) setForeground({title: notice.title || "Lift & Learn", body: notice.body || "", data: notice.data || {}}); },
      onError: (error) => { reportError(error, "push_registration"); if (!disposed) setPushError(true); },
      onToken: (token) => {
        if (!disposed) void savePushToken(token).then(() => { if (!disposed) setPushError(false); }).catch((error) => { reportError(error, "push_registration"); if (!disposed) setPushError(true); });
      },
      onNotification: (data) => { if (!disposed) { track("notification_opened", {source: "native"}); navigate(notificationRoute(data)); } },
      onRoute: navigate,
      onShare: () => {
        navigate("/home");
        window.dispatchEvent(new Event("ll:incoming-share"));
      },
      onResume: refresh,
    })
      .then((stop) => {
        if (disposed) stop();
        else cleanup = stop;
      })
      .catch((error) => { reportError(error, "push_registration"); if (!disposed) setPushError(true); });
    return () => {
      disposed = true;
      cleanup?.();
      window.removeEventListener("focus", refreshPermission);
      window.removeEventListener("ll:notification-permission-change", refreshPermission);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [userId, cache, navigate, pushRefresh]);
  useEffect(() => {
    setForeground(null);
    setPushError(false);
    if (!userId || isNative() || permission !== "granted") return;
    let disposed = false;
    let cleanup: (() => void) | undefined;
    void watchBrowserPush({
      onForeground: payload => { if (!disposed) setForeground(payload); },
      onClick: data => { if (!disposed) { track("notification_opened", {source: "browser"}); navigate(notificationRoute(data)); } },
      onToken: token => { if (!disposed) void savePushToken(token).catch((error) => { reportError(error, "push_registration"); if (!disposed) setPushError(true); }); },
    }).then(stop => { if (disposed) stop(); else cleanup = stop; }).catch((error) => { reportError(error, "push_registration"); if (!disposed) setPushError(true); });
    return () => { disposed = true; cleanup?.(); };
  }, [userId, permission, pushRefresh, navigate]);
  return (
    <Suspense fallback={<MascotLoading />}>
      <div className="app">
        <HostedUpdates />
        {recoveryError && <p role="alert">{recoveryError}</p>}
        <header className="mast">
          <Link to={links.home()} className="brand">
            Lift &amp; Learn
          </Link>
          {userId && approved && (
            <div className="header-actions">
              <NavLink to={links.notifications()} className="header-icon" aria-label="Notifications">
                <Icon name="bell" />
              </NavLink>
              <NavLink to={links.profile()} className="header-avatar" aria-label="Your profile">
                <PersonAvatar media={account.data?.profile?.avatar_url} name={account.data?.profile?.display_name ?? ""} size="sm" />
              </NavLink>
            </div>
          )}
        </header>
        {location.pathname === "/privacy" ? (
          <Privacy />
        ) : !configured ? (
          <main className="page">
            <h1>Connect Lift &amp; Learn</h1>
            <p>
              This build needs its public Supabase and Firebase configuration.
              No demo accounts or production data are substituted.
            </p>
          </main>
        ) : userId === undefined ? (
          <MascotLoading />
        ) : !userId ? (
          <Auth />
        ) : account.isPending ? (
          <MascotLoading label="Loading your season…" />
        ) : account.error && !account.data ? (
          <main className="page">
            <p role="alert">{account.error.message}</p>
            <button onClick={() => account.refetch()}>Try again</button>
            <button className="secondary" onClick={() => void signOutAccount()}>
              Sign out
            </button>
          </main>
        ) : (
          <>
            {pushError && <aside className="permission-banner" role="alert"><Link to="/notification-preferences">Notification registration failed. Open preferences and save again to retry.</Link></aside>}
            {foreground && (
              <aside className="permission-banner" role="status">
                <strong>{foreground.title}</strong>
                {foreground.body && <p>{foreground.body}</p>}
                <div className="header-actions">
                  <Link to={notificationRoute(foreground.data)} onClick={() => setForeground(null)}>View</Link>
                  <button className="secondary" style={{width: "auto"}} onClick={() => setForeground(null)}>Dismiss</button>
                </div>
              </aside>
            )}
            {permission === "denied" && (
              <aside className="permission-banner">
                <Link to="/notification-preferences">
                  Notifications are off on this device. Review your preferences
                  to enable them.
                </Link>
              </aside>
            )}
            {approved && !preferencesDone ? (
              <Preferences onComplete={preferencesComplete} />
            ) : pendingOnly ? (
              <Suspense fallback={<RouteSkeleton path={location.pathname} />}><Routes>
                <Route
                  path="/onboarding/*"
                  element={<Onboarding key={userId} onComplete={startFirstPost} />}
                />
                {/* Notifications tell applicants when they're approved. */}
                <Route
                  path="/notification-preferences"
                  element={<Preferences onComplete={preferencesComplete} />}
                />
                <Route path="*" element={<Navigate to="/onboarding" replace />} />
              </Routes></Suspense>
            ) : (
              <Suspense fallback={<RouteSkeleton path={location.pathname} />}><Routes>
                <Route
                  path="/onboarding/*"
                  element={<Onboarding key={userId} onComplete={startFirstPost} />}
                />
                <Route
                  path="/profile/*"
                  element={<Profile key={userId} onComplete={complete} />}
                />
                <Route
                  path="/notification-preferences"
                  element={<Preferences onComplete={preferencesComplete} />}
                />
                <Route path="/notifications" element={<Inbox />} />
                <Route path="/settings" element={<Settings />} />
                <Route
                  path="/review-requests"
                  element={
                    canManage ? <ApplicationQueue /> : <Navigate to="/home" />
                  }
                />
                <Route
                  path="/manage/*"
                  element={canManage ? <Manage /> : <Navigate to="/home" />}
                />
                <Route
                  path="*"
                  element={
                    !approved ? (
                      <Navigate to="/onboarding" />
                    ) : !preferencesDone ? (
                      <Preferences onComplete={preferencesComplete} />
                    ) : (
                      <Routes>
                        <Route path="/discuss/*" element={<Discuss />} />
                        <Route path="*" element={<Feed />} />
                      </Routes>
                    )
                  }
                />
              </Routes></Suspense>
            )}
            {approved && preferencesDone && (
              <nav className="bottom-nav" aria-label="Main navigation">
                <NavLink to={links.home()}><Icon name="home" />Home</NavLink>
                <button className="nav-post" aria-label="Create post" aria-haspopup="dialog" onPointerDown={() => { void loadComposer().catch(() => {}); }} onClick={() => setPosting(`${userId}:${location.key}`)}><span aria-hidden="true"><Icon name="plus" /></span>Post</button>
                <NavLink to={links.discuss()}><Icon name="discuss" />Discuss</NavLink>
                {canManage && (
                  <NavLink to={links.manage()}><Icon name="shield" />
                    {account.data?.admin ? "Admin" : "Manage"}
                  </NavLink>
                )}
              </nav>
            )}
            {approved && preferencesDone && posting === `${userId}:${location.key}` && <Suspense fallback={<ComposerLoading />}><CreatePost key={userId} onClose={() => setPosting(null)} /></Suspense>}
          </>
        )}
      </div>
    </Suspense>
  );
}
